import React, {useContext, useMemo, useState} from 'react';
import Popup from '@src/components/Popup/Popup';
import Translate from '@src/components/Translate/Translate';
import {LocaleContext} from '@src/contexts/LocaleContext';
import trans from '@src/helpers/trans';
import RenderIf from '@src/components/RenderIf/RenderIf';
import app from '@src/helpers/app';
import {FormFieldOption} from '@src/@types/components/Form';
import CustomSelect from '@src/components/Fields/CustomSelect';
import {notNull} from '@src/helpers/ts/notNull';
import CopyButton from '@views/external-applications/fields/CopyButton';

export default function AddNewTokenPopup(props: {
    controllerName: string,
    modelId: string | number,
    closePopup: () => void,
    onCreate?: (id: string | number, tokenName: string, abilities: string[]) => void,
    abilitiesOptions?: FormFieldOption[],
}) {
    const {locale} = useContext(LocaleContext);
    const [name, setName] = useState('');
    const [abilities, setAbilities] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | undefined>();
    const [token, setToken] = useState<string | undefined>();

    const [options, onAbilitiesChanged] = useMemo(() => {
        const onAbilitiesChanged = (values: Array<string | number | null>) => {
            setAbilities(values.filter(notNull) as string[]);
        };

        const options = props.abilitiesOptions?.map(item => {
            return {
                ...item,
                label: trans(
                        `ExternalApplications.token.abilities.${item.label.replace('.', '-')}`,
                        locale
                ),
            };
        });

        return [options, onAbilitiesChanged];
    }, [locale, props.abilitiesOptions]);

    return <Popup
            title="ExternalApplications.add-token-popup.title"
            closePopup={props.closePopup}
            footer={
                <div className="text-end">
                    <Translate<HTMLButtonElement>
                            tag="button"
                            message="ExternalApplications.add-token-popup.buttons.cancel"
                            type="button"
                            className="btn btn-link me-2"
                            disabled={isLoading}
                            onClick={props.closePopup}
                    />

                    <button
                            type="button"
                            className="btn btn-primary"
                            disabled={isLoading || token != null}
                            onClick={() => {
                                if (isLoading) {
                                    return;
                                }

                                setIsLoading(true);
                                setErrorMessage(undefined);

                                app('http').route('external-applications.tokens.store', {
                                    id: String(props.modelId),
                                }, {
                                    headers: {'content-type': 'application/json'},
                                    body: {
                                        'controller-name': props.controllerName,
                                        name,
                                        abilities,
                                    },
                                }).then(value => {
                                    setToken(value?.token ?? undefined);
                                    if (props.onCreate) {
                                        props.onCreate(value?.id ?? 0, name, abilities);
                                    }
                                }).catch(err => {
                                    setErrorMessage(err?.message ?? 'Something went wrong');
                                }).finally(() => {
                                    setIsLoading(false);
                                });
                            }}
                    >
                        <Translate message="ExternalApplications.add-token-popup.buttons.create"/>
                    </button>
                </div>
            }
    >
        <RenderIf expression={errorMessage}>
            <div className="alert alert-danger">{errorMessage}</div>
        </RenderIf>

        <RenderIf expression={!token}>
            <div className="mb-3">
                <Translate<HTMLLabelElement>
                        tag="label"
                        message="ExternalApplications.fields.token.name"
                        htmlFor="ExternalApplications.fields.token.name"
                        className="form-label"
                />
                <input
                        id="ExternalApplications.fields.token.name"
                        value={name}
                        onChange={e => setName(e.currentTarget.value ?? '')}
                        type="text"
                        placeholder={trans('ExternalApplications.fields.token.name', locale)}
                        className="form-control"
                />
            </div>
            <div>
                <Translate<HTMLLabelElement>
                        tag="label"
                        message="ExternalApplications.fields.token.abilities"
                        htmlFor="ExternalApplications.fields.token.abilities"
                        className="form-label"
                />

                <CustomSelect<true>
                        inputId="ExternalApplications.fields.token.abilities"
                        label="ExternalApplications.fields.token.abilities"
                        value={abilities}
                        onChange={onAbilitiesChanged}
                        options={options}
                        multiple
                />
            </div>
        </RenderIf>

        <RenderIf expression={token}>
            <Translate<HTMLLabelElement>
                    tag="label"
                    message="ExternalApplications.add-token-popup.your-token-label"
                    htmlFor="ExternalApplications.add-token-popup.your-token-label"
                    className="form-label"
            />
            <div className="input-group">
                <input
                        id="ExternalApplications.add-token-popup.your-token-label"
                        value={token}
                        type="text"
                        placeholder={trans('ExternalApplications.add-token-popup.your-token-label', locale)}
                        className="form-control"
                        readOnly
                />
                <CopyButton text={token as string}/>
            </div>
        </RenderIf>
    </Popup>;
}
